/* eslint-disable */
import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";

/**
 * PerkAnalyticsCard — Monthly per-partner scan breakdown (Session 60.1).
 *
 * Fetches /api/admin/hours-perks/analytics and renders a compact pivot
 * table: partner rows × month columns, sorted by total. Bar-height cells
 * make trends legible without a chart library. Partners with a perk but
 * zero scans are still shown so admins can see under-performing perks.
 */
function PerkAnalyticsCard() {
  const [months, setMonths] = useState(6);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (n = months) => {
    setLoading(true);
    try {
      const r = await api.get("/admin/hours-perks/analytics", { params: { months: n } });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Analytics load failed");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(months); /* eslint-disable-next-line */ }, [months]);

  // Cell intensity: 0 = neutral, up to `peak` = full gold. Non-linear so
  // a single scan is still visible against zero.
  const peakScans = (data?.partners || []).reduce((mx, p) => {
    for (const k of Object.keys(p.by_month || {})) mx = Math.max(mx, p.by_month[k] || 0);
    return mx;
  }, 0);
  const cellStyle = (n) => {
    if (!n) return { background: "transparent", color: "var(--m-muted)" };
    const t = Math.min(1, 0.15 + 0.85 * Math.sqrt(n / Math.max(1, peakScans)));
    return {
      background: `rgba(212,168,75,${(t * 0.45).toFixed(3)})`,
      color: "var(--m-text)",
      fontWeight: 600,
    };
  };

  const fmtMonthHeader = (key) => {
    // "2026-02" → "Feb '26"
    const [y, m] = (key || "").split("-");
    const monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    return `${monthNames[Number(m) - 1] || m} '${(y || "").slice(-2)}`;
  };

  return (
    <div className="m-card" style={{ marginBottom: 20 }} data-testid="perk-analytics">
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div>
          <h3 className="m-side-title" style={{ marginTop: 0, marginBottom: 4 }}>
            Perk <em>analytics</em>
          </h3>
          <div className="m-muted small">Valid QR scans per partner, by month.</div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <label className="m-field" style={{ margin: 0, minWidth: 140 }}>
            <span>Window</span>
            <select value={months} onChange={(e) => setMonths(Number(e.target.value))} data-testid="perk-analytics-window">
              <option value={3}>Last 3 months</option>
              <option value={6}>Last 6 months</option>
              <option value={12}>Last 12 months</option>
            </select>
          </label>
        </div>
      </div>

      {loading ? (
        <div className="m-loading-inline" style={{ marginTop: 12 }}>Loading analytics…</div>
      ) : !data || data.partners.length === 0 ? (
        <div className="m-empty small" style={{ marginTop: 12 }}>
          No partner-tagged scans in this window yet.
        </div>
      ) : (
        <div style={{ overflowX: "auto", marginTop: 12 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: "left" }}>
                <th style={{ padding: "8px 6px", borderBottom: "1px solid rgba(212,168,75,0.2)" }}>Partner</th>
                <th style={{ padding: "8px 6px", borderBottom: "1px solid rgba(212,168,75,0.2)" }}>Perk</th>
                {data.months.map((k) => (
                  <th key={k} style={{ padding: "8px 6px", borderBottom: "1px solid rgba(212,168,75,0.2)", textAlign: "center", fontWeight: 500, color: "var(--m-muted)", whiteSpace: "nowrap" }}>
                    {fmtMonthHeader(k)}
                  </th>
                ))}
                <th style={{ padding: "8px 6px", borderBottom: "1px solid rgba(212,168,75,0.2)", textAlign: "right" }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {data.partners.map((p) => (
                <tr key={p.partner_id} data-testid={`perk-analytics-row-${p.partner_id}`} style={{ borderBottom: "1px solid rgba(212,168,75,0.06)" }}>
                  <td style={{ padding: "10px 6px", fontWeight: 600 }}>{p.partner_name}</td>
                  <td style={{ padding: "10px 6px" }}>
                    {p.perk_label ? (
                      <span style={{ color: "var(--gold)", fontWeight: 600 }}>{p.perk_label}</span>
                    ) : (
                      <span className="m-muted small" style={{ fontStyle: "italic" }}>not set</span>
                    )}
                  </td>
                  {data.months.map((k) => {
                    const n = (p.by_month || {})[k] || 0;
                    return (
                      <td
                        key={k}
                        data-testid={`perk-analytics-cell-${p.partner_id}-${k}`}
                        style={{
                          padding: "10px 6px",
                          textAlign: "center",
                          borderRadius: 4,
                          ...cellStyle(n),
                        }}
                      >
                        {n || "—"}
                      </td>
                    );
                  })}
                  <td style={{ padding: "10px 6px", textAlign: "right", fontFamily: "Georgia,serif", fontSize: 18, color: "var(--gold)" }}>
                    {p.total}
                  </td>
                </tr>
              ))}
              <tr>
                <td colSpan={2 + data.months.length} style={{ padding: "12px 6px 0", textAlign: "right", color: "var(--m-muted)", fontStyle: "italic" }}>
                  Grand total this window:
                </td>
                <td style={{ padding: "12px 6px 0", textAlign: "right", fontFamily: "Georgia,serif", fontSize: 20, color: "var(--gold)" }} data-testid="perk-analytics-grand-total">
                  {data.grand_total}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * AdminHoursScans — Métier Hours QR scan log (Option A tracking).
 * Shows totals for a selectable window, the top members, and the most recent
 * scans. Plus a "Send weekly digest now" button that reuses the /admin/
 * hours-scans/send-digest-now endpoint (email to all ADMIN_EMAILS).
 */
export default function AdminHoursScans() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const load = async (d = days) => {
    setLoading(true);
    try {
      const r = await api.get("/admin/hours-scans", { params: { days: d, limit: 200 } });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(days); /* eslint-disable-next-line */ }, [days]);

  const sendDigest = async () => {
    setSending(true);
    try {
      const r = await api.post("/admin/hours-scans/send-digest-now");
      if (r.data?.sent) {
        toast.success(`Digest sent to ${r.data.recipients} admin${r.data.recipients === 1 ? "" : "s"} · ${r.data.total_scans} scans`);
      } else {
        toast.info(`Digest not sent: ${r.data?.reason || "unknown"}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Digest send failed");
    } finally {
      setSending(false);
    }
  };

  const fmt = (iso) => {
    try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
    catch { return iso; }
  };

  return (
    <div className="m-admin-section" data-testid="admin-hours-scans">
      <div className="m-card" style={{marginBottom: 16}}>
        <span className="m-eyebrow" style={{color: "var(--gold)"}}>Métier Hours · QR redemption tracking</span>
        <h2 className="rec-h2" style={{margin: "6px 0 12px"}}>Founding member scan log.</h2>
        <p className="m-muted" style={{maxWidth: 640, marginTop: -4}}>
          Every time a partner café scans a member&apos;s Métier Hours QR, we log the scan here.
          Weekly digest goes out to all admins every Monday 09:00 UTC — you can also force-send below.
        </p>

        <div style={{display: "flex", gap: 12, alignItems: "center", marginTop: 16, flexWrap: "wrap"}}>
          <label className="m-field" style={{margin: 0, minWidth: 160}}>
            <span>Window</span>
            <select value={days} onChange={(e) => setDays(Number(e.target.value))} data-testid="hours-scans-window">
              <option value={1}>Last 24 hours</option>
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
              <option value={365}>Last year</option>
            </select>
          </label>
          <button className="m-btn-ghost" onClick={() => load(days)} disabled={loading} data-testid="hours-scans-refresh">
            {loading ? "Loading…" : "Refresh"}
          </button>
          <button className="m-btn-gold" onClick={sendDigest} disabled={sending} data-testid="hours-scans-send-digest">
            {sending ? "Sending…" : "Send weekly digest now →"}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="m-loading-inline">Loading scans…</div>
      ) : !data ? (
        <div className="m-empty">Could not load scan data.</div>
      ) : (
        <>
          <div className="m-stats-grid" style={{marginBottom: 20}}>
            <div className="m-stat" data-testid="hours-scans-total">
              <span className="m-stat-num">{data.count}</span>
              <span className="m-stat-label">Total scans</span>
            </div>
            <div className="m-stat" data-testid="hours-scans-valid">
              <span className="m-stat-num">{data.scans.filter(s => s.token_valid).length}</span>
              <span className="m-stat-label">Valid (in shown)</span>
            </div>
            <div className="m-stat" data-testid="hours-scans-invalid">
              <span className="m-stat-num">{data.scans.filter(s => !s.token_valid).length}</span>
              <span className="m-stat-label">Invalid / bad token</span>
            </div>
            <div className="m-stat" data-testid="hours-scans-unique-members">
              <span className="m-stat-num">{data.top_members.length}</span>
              <span className="m-stat-label">Unique members</span>
            </div>
          </div>

          <PerkAnalyticsCard />

          {data.top_members.length > 0 && (
            <div className="m-card" style={{marginBottom: 20}}>
              <h3 className="m-side-title" style={{marginTop: 0}}>Top <em>members</em></h3>
              <div className="m-admin-list" style={{marginTop: 8}}>
                {data.top_members.map(m => (
                  <div key={m.user_id} className="m-admin-row" data-testid={`hours-scan-member-${m.user_id}`} style={{padding: "10px 0"}}>
                    <div className="m-admin-row-info">
                      <div className="m-admin-row-name">{m.name}</div>
                      <div className="m-muted small">{m.user_id}</div>
                    </div>
                    <div style={{fontFamily: "Georgia,serif", fontSize: 22, color: "var(--gold)"}}>{m.scans}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="m-card">
            <h3 className="m-side-title" style={{marginTop: 0}}>Recent <em>scans</em></h3>
            {data.scans.length === 0 ? (
              <div className="m-empty small" style={{marginTop: 12}}>No scans in this window yet.</div>
            ) : (
              <div className="m-admin-list" style={{marginTop: 8}}>
                {data.scans.map(s => (
                  <div key={s.scan_id} className="m-admin-row" data-testid={`hours-scan-${s.scan_id}`} style={{padding: "10px 0", borderBottom: "1px solid rgba(212,168,75,0.08)"}}>
                    <div className="m-admin-row-info">
                      <div className="m-admin-row-name">
                        {s.member_name || (s.token_valid ? "Member" : "Unknown QR")}
                        {s.founding_member && <span className="m-badge-founding tiny" style={{marginLeft: 8}}>Founding</span>}
                        {!s.token_valid && <span className="m-badge-admin tiny" style={{marginLeft: 8, background: "#b23636"}}>Invalid</span>}
                      </div>
                      <div className="m-muted small">
                        {s.member_email || "—"} · {fmt(s.scanned_at)} · IP {s.ip || "—"}
                      </div>
                      <div className="m-muted small" style={{opacity: 0.6, marginTop: 2, wordBreak: "break-all"}}>
                        UA: {(s.user_agent || "").slice(0, 100) || "—"}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
